import React, { useState, useEffect } from 'react';
import { Card, DataTable, MetricCard, Btn } from '../../components/ui';
import { Icon } from '../../icons';
import AmenityService from '../../api/amenity';
import { toast } from 'react-hot-toast';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const EmptyLineIcon = () => (
  <svg className="w-20 h-20 mb-4 text-primary/20 dark:text-primary/10 drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 12l3-3 3 3 4-4m-8 13h8m-8-13V5a2 2 0 012-2h4a2 2 0 012 2v2M3 21h18M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16" />
  </svg>
);

const EmptyPieIcon = () => (
  <svg className="w-20 h-20 mb-4 text-amber-500/20 dark:text-amber-500/10 drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
  </svg>
);

const EmptyBarIcon = () => (
  <svg className="w-20 h-20 mb-4 text-green-500/20 dark:text-green-500/10 drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

function PureSvgAreaChart({ data, xKey = "date", yKey = "total_revenue" }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) return null;

  const width = 500;
  const height = 220;
  const paddingLeft = 65;
  const paddingRight = 20;
  const paddingTop = 32;
  const paddingBottom = 32;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const baseY = paddingTop + chartHeight;

  const values = data.map(d => Number(d[yKey]) || 0);
  const rawMax = Math.max(...values, 10);
  const maxVal = rawMax * 1.25;
  const minVal = 0;

  const points = data.map((d, idx) => {
    const val = Number(d[yKey]) || 0;
    const x = data.length === 1 
      ? paddingLeft + chartWidth / 2 
      : paddingLeft + (idx / (data.length - 1)) * chartWidth;
    const y = paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
    return { x, y, data: d, val };
  });

  let pathD = '';
  let areaD = '';

  if (points.length === 1) {
    const pt = points[0];
    const halfSpan = Math.min(80, chartWidth / 3);
    const xLeft = Math.max(paddingLeft, pt.x - halfSpan);
    const xRight = Math.min(width - paddingRight, pt.x + halfSpan);
    pathD = `M ${xLeft} ${baseY} Q ${pt.x} ${pt.y}, ${xRight} ${baseY}`;
    areaD = `M ${xLeft} ${baseY} Q ${pt.x} ${pt.y}, ${xRight} ${baseY} Z`;
  } else {
    pathD = points.reduce((acc, pt, i) => {
      if (i === 0) return `M ${pt.x} ${pt.y}`;
      const prev = points[i - 1];
      const cx1 = prev.x + (pt.x - prev.x) / 2;
      const cy1 = prev.y;
      const cx2 = prev.x + (pt.x - prev.x) / 2;
      const cy2 = pt.y;
      return `${acc} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${pt.x} ${pt.y}`;
    }, '');
    areaD = `${pathD} L ${points[points.length - 1].x} ${baseY} L ${points[0].x} ${baseY} Z`;
  }

  const yTicks = [
    { label: `₹${Math.round(rawMax).toLocaleString()}`, y: paddingTop + chartHeight - ((rawMax - minVal) / (maxVal - minVal)) * chartHeight },
    { label: `₹${Math.round(rawMax / 2).toLocaleString()}`, y: paddingTop + chartHeight - ((rawMax / 2 - minVal) / (maxVal - minVal)) * chartHeight },
    { label: `₹0`, y: baseY },
  ];

  const step = Math.max(1, Math.floor(data.length / 5));
  const xTicks = points.filter((_, i) => i === 0 || i === points.length - 1 || i % step === 0);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const relX = (mouseX / rect.width) * width;
    let closest = 0;
    let minDist = Infinity;
    points.forEach((pt, i) => {
      const dist = Math.abs(pt.x - relX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    setHoveredIndex(closest);
  };

  const hoveredPt = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <div className="relative w-full h-full select-none" onMouseLeave={() => setHoveredIndex(null)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-full overflow-visible"
        onMouseMove={handleMouseMove}
      >
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={paddingLeft}
              y1={tick.y}
              x2={width - paddingRight}
              y2={tick.y}
              stroke="currentColor"
              className="text-slate-200 dark:text-slate-800"
              strokeDasharray="3 3"
            />
            <text
              x={paddingLeft - 8}
              y={tick.y + 4}
              textAnchor="end"
              className="fill-slate-400 text-[10px] font-medium"
            >
              {tick.label}
            </text>
          </g>
        ))}

        <path d={areaD} fill="url(#areaGradient)" />
        <path d={pathD} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" />

        {points.length === 1 && (
          <g>
            <line
              x1={points[0].x}
              y1={points[0].y}
              x2={points[0].x}
              y2={baseY}
              stroke="#6366f1"
              strokeWidth="1.5"
              strokeDasharray="3 3"
              opacity="0.6"
            />
            <rect
              x={points[0].x - 38}
              y={Math.max(6, points[0].y - 28)}
              width="76"
              height="20"
              rx="6"
              fill="#4f46e5"
              className="drop-shadow-sm"
            />
            <text
              x={points[0].x}
              y={Math.max(6, points[0].y - 28) + 14}
              textAnchor="middle"
              className="fill-white text-[10px] font-bold"
            >
              ₹{Number(points[0].val).toLocaleString()}
            </text>
          </g>
        )}

        {points.map((pt, i) => (
          <circle
            key={i}
            cx={pt.x}
            cy={pt.y}
            r={hoveredIndex === i ? "6" : points.length === 1 ? "5" : "3.5"}
            fill={hoveredIndex === i ? "#4f46e5" : "#6366f1"}
            stroke="#fff"
            strokeWidth="2"
            className="transition-all duration-150 drop-shadow-sm"
          />
        ))}

        {hoveredPt && points.length > 1 && (
          <line
            x1={hoveredPt.x}
            y1={paddingTop}
            x2={hoveredPt.x}
            y2={baseY}
            stroke="#4f46e5"
            strokeWidth="1.5"
            strokeDasharray="2 2"
            opacity="0.6"
          />
        )}

        {xTicks.map((pt, i) => (
          <text
            key={i}
            x={pt.x}
            y={height - 8}
            textAnchor="middle"
            className="fill-slate-400 text-[10px] font-medium"
          >
            {pt.data[xKey]}
          </text>
        ))}
      </svg>

      {hoveredPt && points.length > 1 && (
        <div
          className="absolute pointer-events-none z-10 bg-slate-900/95 dark:bg-slate-800/95 text-white text-xs px-3 py-2 rounded-lg shadow-xl border border-slate-700/80 -translate-x-1/2 -translate-y-full mb-2 transition-all duration-75"
          style={{
            left: `${(hoveredPt.x / width) * 100}%`,
            top: `${(hoveredPt.y / height) * 100}%`,
          }}
        >
          <div className="font-bold text-slate-200 mb-0.5">{hoveredPt.data[xKey]}</div>
          <div className="text-emerald-400 font-semibold">
            Revenue: ₹{Number(hoveredPt.val).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReportsView({ amenities }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ 
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
    end_date: new Date().toISOString().split('T')[0] 
  });
  
  const [reportData, setReportData] = useState({
    bookings: { data: [], total: 0, current_page: 1, last_page: 1 },
    revenue: { by_amenity: [], trend: [], total_revenue: 0 },
    utilisation: []
  });

  const [bookingPage, setBookingPage] = useState(1);
  const [bookingStatusFilter, setBookingStatusFilter] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [bookingsRes, revenueRes, utilRes] = await Promise.all([
        AmenityService.getReportBookings({ 
          start_date: dateRange.start_date, 
          end_date: dateRange.end_date,
          page: bookingPage,
          booking_status: bookingStatusFilter,
          search: bookingSearch
        }),
        AmenityService.getReportRevenue({ 
          start_date: dateRange.start_date, 
          end_date: dateRange.end_date 
        }),
        AmenityService.getReportUtilisation({ 
          start_date: dateRange.start_date, 
          end_date: dateRange.end_date 
        })
      ]);

      setReportData({
        bookings: bookingsRes.data,
        revenue: revenueRes.data,
        utilisation: utilRes.data
      });
    } catch (err) {
      toast.error('Failed to load reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange, bookingPage, bookingStatusFilter, bookingSearch]);

  const handleDatePreset = (preset) => {
    const today = new Date();
    let start = new Date();
    
    switch(preset) {
      case 'today':
        break;
      case 'this_week':
        start.setDate(today.getDate() - today.getDay());
        break;
      case 'this_month':
        start.setDate(1);
        break;
      case 'last_30':
        start.setDate(today.getDate() - 30);
        break;
      case 'this_year':
        start.setMonth(0, 1);
        break;
      default:
        start.setDate(1);
    }
    
    setDateRange({
      start_date: start.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    });
  };

  const renderOverview = () => {
    const totalBookings = reportData.bookings.total;
    const totalRevenue = reportData.revenue.total_revenue;
    const avgUtilisation = reportData.utilisation.length > 0 
      ? reportData.utilisation.reduce((acc, curr) => acc + curr.utilisation_percentage, 0) / reportData.utilisation.length
      : 0;
      
    return (
      <div className="space-y-6 fade-up">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <MetricCard label="Total Revenue" value={`₹${totalRevenue.toLocaleString()}`} icon="money" accent="green" />
          <MetricCard label="Total Bookings" value={totalBookings} icon="calendar" accent="blue" />
          <MetricCard label="Avg Utilisation" value={`${avgUtilisation.toFixed(1)}%`} icon="trend_up" accent="amber" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card
            title="Revenue Trend"
            subtitle="Daily earnings over selected period"
            action={totalRevenue > 0 ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">Total: ₹{totalRevenue.toLocaleString()}</span> : null}
          >
            <div className="w-full h-[210px] sm:h-[240px]">
              {reportData.revenue.trend.length > 0 ? (
                <PureSvgAreaChart data={reportData.revenue.trend} xKey="date" yKey="total_revenue" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-transparent to-slate-50/50 dark:to-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700/60 m-1">
                  <EmptyLineIcon />
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No Revenue Trend Found</p>
                  <p className="text-xs mt-1 text-slate-400 dark:text-slate-500 max-w-[240px] text-center">There are no paid bookings within the selected date range. Try adjusting the filters above.</p>
                </div>
              )}
            </div>
          </Card>
          
          <Card
            title="Revenue by Amenity"
            subtitle="Breakdown of earnings across amenities"
          >
            <div className="flex flex-col justify-center min-h-[200px]">
              {reportData.revenue.by_amenity.length > 0 ? (
                <div className="w-full h-full pr-1 overflow-y-auto flex flex-col gap-4 py-1 max-h-[240px]">
                  {[...reportData.revenue.by_amenity]
                    .sort((a, b) => b.total_revenue - a.total_revenue)
                    .map((entry, index) => {
                      const percentage = totalRevenue > 0 ? ((entry.total_revenue / totalRevenue) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex flex-col gap-1.5 group">
                          <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="font-semibold text-slate-700 dark:text-slate-200 group-hover:text-primary transition-colors flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-primary/40 group-hover:bg-primary transition-colors"></span>
                              {entry.amenity_name}
                            </span>
                            <span className="font-bold text-slate-700 dark:text-slate-200">
                              ₹{parseFloat(entry.total_revenue).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 pl-4">
                            <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-800/80 rounded-full overflow-hidden shadow-inner">
                              <div 
                                className="h-full rounded-full bg-gradient-to-r from-primary/80 to-purple-500/80 group-hover:from-primary group-hover:to-purple-500 transition-all duration-1000 ease-out"
                                style={{ width: `${Math.max(1, percentage)}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-slate-400 dark:text-slate-500 w-12 text-right">{percentage}%</span>
                          </div>
                        </div>
                      );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full w-full bg-gradient-to-b from-transparent to-slate-50/50 dark:to-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700/60 m-1 py-8">
                  <EmptyPieIcon />
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No Revenue Data</p>
                  <p className="text-xs mt-1 text-slate-400 dark:text-slate-500 max-w-[240px] text-center">Select a different period to see the revenue breakdown by amenity.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  const renderBookingsReport = () => (
    <div className="space-y-4 fade-up">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input 
          type="text" 
          placeholder="Search by ID, Name or Unit..." 
          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 w-full sm:w-64"
          value={bookingSearch}
          onChange={(e) => setBookingSearch(e.target.value)}
        />
        <select 
          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 w-full sm:w-48"
          value={bookingStatusFilter}
          onChange={(e) => { setBookingStatusFilter(e.target.value); setBookingPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <Card>
        <DataTable 
          cols={["Booking ID", "Date", "Amenity", "Booked By", "Unit", "Amount", "Status"]}
          rows={(reportData.bookings.data || []).map(b => [
            b.booking_id,
            b.date,
            b.amenity?.name || "-",
            b.booked_by,
            b.unit,
            `₹${parseFloat(b.amount || 0).toLocaleString()}`,
            <span key={b.id} className={`capitalize text-xs font-semibold px-2.5 py-1 rounded-full ${b.booking_status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : b.booking_status === 'cancelled' ? 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>
              {b.booking_status}
            </span>
          ])}
          emptyMsg="No bookings found for the selected filters."
          footer={reportData.bookings.last_page > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted">
                Showing page {reportData.bookings.current_page} of {reportData.bookings.last_page} ({reportData.bookings.total} total)
              </div>
              <div className="flex gap-1">
                <Btn sm variant="secondary" onClick={() => setBookingPage(p => Math.max(1, p - 1))} disabled={bookingPage === 1}>Prev</Btn>
                <Btn sm variant="secondary" onClick={() => setBookingPage(p => Math.min(reportData.bookings.last_page, p + 1))} disabled={bookingPage === reportData.bookings.last_page}>Next</Btn>
              </div>
            </div>
          )}
        />
      </Card>
    </div>
  );

  const renderUtilisationReport = () => (
    <div className="space-y-6 fade-up">
      <Card title="Amenity Utilisation">
        <DataTable 
          cols={["Amenity", "Total Bookings", "Available Hours", "Booked Hours", "Utilisation %"]}
          rows={reportData.utilisation.map(u => {
            const p = u.utilisation_percentage;
            const displayWidth = Math.max(2, p);
            const colorClass = p > 70 ? 'from-rose-500 to-red-500' : p > 40 ? 'from-amber-400 to-orange-500' : 'from-primary to-purple-500';
            
            return [
              <span key={`name-${u.amenity_name}`} className="font-semibold text-slate-700 dark:text-slate-200">{u.amenity_name}</span>,
              u.total_bookings,
              `${u.available_hours} hrs`,
              `${u.booked_hours} hrs`,
              <div key={`prog-${u.amenity_name}`} className="flex items-center gap-3 min-w-[120px]">
                <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-800/80 rounded-full overflow-hidden shadow-inner">
                  <div 
                    className={`h-full rounded-full bg-gradient-to-r ${colorClass} transition-all duration-1000 ease-out`} 
                    style={{width: `${displayWidth}%`}}
                  />
                </div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 w-10 text-right">{p}%</span>
              </div>
            ];
          })}
          compact
          emptyMsg="No active amenities or bookings available to calculate utilisation."
        />
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Filters Toolbar */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap gap-2">
          <Btn sm variant={activeTab === 'overview' ? 'primary' : 'secondary'} onClick={() => setActiveTab('overview')}>Overview</Btn>
          <Btn sm variant={activeTab === 'bookings' ? 'primary' : 'secondary'} onClick={() => setActiveTab('bookings')}>Bookings Report</Btn>
          <Btn sm variant={activeTab === 'utilisation' ? 'primary' : 'secondary'} onClick={() => setActiveTab('utilisation')}>Utilisation</Btn>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex gap-1 border-r border-slate-300 dark:border-slate-600 pr-3">
            <button className="text-xs text-primary hover:underline" onClick={() => handleDatePreset('this_week')}>This Week</button>
            <span className="text-muted text-xs">•</span>
            <button className="text-xs text-primary hover:underline" onClick={() => handleDatePreset('this_month')}>This Month</button>
          </div>
          <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/50 transition-all shadow-sm w-full md:w-auto">
            <input 
              type="date" 
              value={dateRange.start_date}
              onChange={(e) => setDateRange(prev => ({...prev, start_date: e.target.value}))}
              className="px-3 py-1.5 bg-transparent border-none outline-none text-sm text-slate-700 dark:text-slate-300 w-full hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
            />
            <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-800/80 px-2 py-2 border-x border-slate-200 dark:border-slate-700">
              <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <input 
              type="date" 
              value={dateRange.end_date}
              onChange={(e) => setDateRange(prev => ({...prev, end_date: e.target.value}))}
              className="px-3 py-1.5 bg-transparent border-none outline-none text-sm text-slate-700 dark:text-slate-300 w-full hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
            />
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-muted animate-pulse">Loading Reports...</div>}

      {!loading && (
        <>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'bookings' && renderBookingsReport()}
          {activeTab === 'utilisation' && renderUtilisationReport()}
        </>
      )}
    </div>
  );
}
